import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import './bankBalance.css';
import { getToken } from '../auth';

const BankBalance = () => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [editingAccountId, setEditingAccountId] = useState(null);
  const [accountName, setAccountName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [startingBalance, setStartingBalance] = useState('0.00');
  const [transactionAccountId, setTransactionAccountId] = useState('');
  const [transactionType, setTransactionType] = useState('deposit');
  const [transactionAmount, setTransactionAmount] = useState('');
  const [transactionNotes, setTransactionNotes] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [adjustingBalance, setAdjustingBalance] = useState(false);
  const [statementAccount, setStatementAccount] = useState(null);
  const [statement, setStatement] = useState(null);
  const [statementLoading, setStatementLoading] = useState(false);
  const [statementError, setStatementError] = useState('');
  const [statementStartDate, setStatementStartDate] = useState('');
  const [statementEndDate, setStatementEndDate] = useState('');

  const getAuthConfig = useCallback(() => {
    const token = getToken();
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, []);

  const resetForm = useCallback(() => {
    setEditingAccountId(null);
    setAccountName('');
    setAccountNumber('');
    setStartingBalance('0.00');
  }, []);

  const populateForm = useCallback((account) => {
    setEditingAccountId(account.id);
    setAccountName(account.account_name || '');
    setAccountNumber(account.account_number || '');
    setStartingBalance(parseFloat(account.balance || 0).toFixed(2));
  }, []);

  const fetchBankAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await axios.get('/bank-accounts', getAuthConfig());
      const accounts = Array.isArray(response.data) ? response.data : [];
      setBankAccounts(accounts);
      setTransactionAccountId(prevAccountId => {
        const stillExists = accounts.some(account => String(account.id) === String(prevAccountId));
        return stillExists ? prevAccountId : (accounts[0]?.id ? String(accounts[0].id) : '');
      });

      if (editingAccountId) {
        const updatedEditingAccount = accounts.find(account => account.id === editingAccountId);
        if (updatedEditingAccount) {
          populateForm(updatedEditingAccount);
        } else {
          resetForm();
        }
      }
    } catch (err) {
      console.error('Error loading bank accounts:', err);
      setError('Unable to load bank account data.');
    } finally {
      setLoading(false);
    }
  }, [editingAccountId, getAuthConfig, populateForm, resetForm]);

  useEffect(() => {
    fetchBankAccounts();
  }, [fetchBankAccounts]);

  const formatAmount = (amount) =>
    parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });

  const totalBalance = bankAccounts.reduce((sum, account) => sum + (parseFloat(account.balance) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!accountName.trim() || !accountNumber.trim()) {
      setError('Please enter both account name and number.');
      return;
    }

    if (!editingAccountId && startingBalance === '') {
      setError('Please enter a starting balance.');
      return;
    }

    const payload = {
      account_name: accountName.trim(),
      account_number: accountNumber.trim()
    };

    try {
      setSubmitting(true);
      if (editingAccountId) {
        await axios.put(`/bank-accounts/${editingAccountId}`, payload, getAuthConfig());
        setMessage('Bank account updated successfully.');
      } else {
        await axios.post('/bank-accounts', {
          ...payload,
          starting_balance: parseFloat(startingBalance) || 0
        }, getAuthConfig());
        setMessage('Bank account created successfully.');
        resetForm();
      }
      await fetchBankAccounts();
    } catch (err) {
      console.error('Error saving bank account:', err);
      const apiMessage = err.response?.data?.error || 'Failed to save bank account.';
      setError(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (account) => {
    const confirmed = window.confirm(`Delete ${account.account_name}?`);
    if (!confirmed) return;

    try {
      setSubmitting(true);
      setMessage('');
      setError('');
      await axios.delete(`/bank-accounts/${account.id}`, getAuthConfig());
      setMessage('Bank account deleted successfully.');
      if (editingAccountId === account.id) {
        resetForm();
      }
      await fetchBankAccounts();
    } catch (err) {
      console.error('Error deleting bank account:', err);
      setError(err.response?.data?.error || 'Failed to delete bank account.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTransaction = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!transactionAccountId) {
      setError('Please select a bank account.');
      return;
    }

    if (!transactionAmount || parseFloat(transactionAmount) <= 0) {
      setError('Please enter an amount greater than 0.');
      return;
    }

    try {
      setAdjustingBalance(true);
      await axios.post(`/bank-accounts/${transactionAccountId}/transactions`, {
        type: transactionType,
        amount: parseFloat(transactionAmount),
        notes: transactionNotes.trim() || null
      }, getAuthConfig());
      setMessage(transactionType === 'deposit' ? 'Deposit recorded successfully.' : 'Withdrawal recorded successfully.');
      setTransactionAmount('');
      setTransactionNotes('');
      await fetchBankAccounts();
    } catch (err) {
      console.error('Error recording bank transaction:', err);
      setError(err.response?.data?.error || 'Failed to record bank transaction.');
    } finally {
      setAdjustingBalance(false);
    }
  };

  const fetchStatement = async (accountId, { startDate, endDate } = {}) => {
    setStatementError('');
    setStatementLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;

      const response = await axios.get(`/bank-accounts/${accountId}/statement`, {
        ...getAuthConfig(),
        params
      });
      setStatement(response.data);
    } catch (err) {
      console.error('Error loading bank statement:', err);
      setStatementError(err.response?.data?.error || 'Failed to load statement.');
    } finally {
      setStatementLoading(false);
    }
  };

  const handleViewStatement = async (account) => {
    setStatementAccount(account);
    setStatement(null);
    setStatementStartDate('');
    setStatementEndDate('');
    await fetchStatement(account.id);
  };

  const handleApplyStatementRange = () => {
    if (!statementAccount) return;
    fetchStatement(statementAccount.id, {
      startDate: statementStartDate,
      endDate: statementEndDate
    });
  };

  const handleShowAllTime = () => {
    if (!statementAccount) return;
    setStatementStartDate('');
    setStatementEndDate('');
    fetchStatement(statementAccount.id);
  };

  const closeStatement = () => {
    setStatementAccount(null);
    setStatement(null);
    setStatementError('');
    setStatementStartDate('');
    setStatementEndDate('');
  };

  return (
    <div className="bank-balance-page">
      <div className="bank-balance-header">
        <div>
          <h2>Bank Balance</h2>
          <p className="bank-balance-description">
            Track balances across your bank accounts, log deposits and withdrawals, and view account statements.
          </p>
        </div>
        {!loading && bankAccounts.length > 0 && (
          <div className="total-balance-pill">
            <span className="label">Total Across Accounts</span>
            <div className="value balance">₹ {formatAmount(totalBalance)}</div>
          </div>
        )}
      </div>

      {message && <div className="message success">{message}</div>}
      {error && <div className="message error">{error}</div>}

      <div className="bank-card">
        <h3>Bank Accounts</h3>
        {loading ? (
          <div className="skeleton-list">
            <div className="skeleton-card" />
            <div className="skeleton-card" />
          </div>
        ) : bankAccounts.length > 0 ? (
          <div className="bank-account-grid">
            {bankAccounts.map(account => (
              <div className="bank-account-card" key={account.id}>
                <div className="bank-account-card-top">
                  <div className="account-avatar">{(account.account_name || '?').charAt(0).toUpperCase()}</div>
                  <div className="account-identity">
                    <div className="account-name">{account.account_name}</div>
                    <div className="account-number">A/C {account.account_number}</div>
                  </div>
                </div>

                <div className="account-balance-row">
                  <span className="label">Current Balance</span>
                  <div className="value balance">₹ {formatAmount(account.balance)}</div>
                </div>

                <div className="account-actions">
                  <button type="button" className="secondary-btn" onClick={() => handleViewStatement(account)} disabled={submitting}>
                    Statement
                  </button>
                  <button type="button" className="secondary-btn" onClick={() => populateForm(account)} disabled={submitting}>
                    Edit
                  </button>
                  <button type="button" className="delete-btn" onClick={() => handleDelete(account)} disabled={submitting}>
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p className="no-account">No bank account exists yet. Create one below to start tracking balances.</p>
          </div>
        )}
      </div>

      <div className="bank-forms-grid">
        <form className="bank-card bank-form" onSubmit={handleSubmit}>
          <div className="form-title-row">
            <h3>{editingAccountId ? 'Edit Bank Account' : 'Create Bank Account'}</h3>
            {editingAccountId && (
              <button type="button" className="link-btn" onClick={resetForm} disabled={submitting}>
                + Add New Account
              </button>
            )}
          </div>

          <div className="form-group">
            <label>Account Name</label>
            <input
              type="text"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="Enter bank account name"
            />
          </div>

          <div className="form-group">
            <label>Account Number</label>
            <input
              type="text"
              value={accountNumber}
              onChange={(e) => setAccountNumber(e.target.value)}
              placeholder="Enter account number"
            />
          </div>

          {!editingAccountId && (
            <div className="form-group">
              <label>Starting Balance</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={startingBalance}
                onChange={(e) => setStartingBalance(e.target.value)}
                placeholder="0.00"
                onWheel={(e) => e.target.blur()}
              />
            </div>
          )}

          <div className="action-buttons">
            <button type="submit" className="submit-btn" disabled={submitting}>
              {submitting ? 'Saving...' : editingAccountId ? 'Update Bank Account' : 'Create Bank Account'}
            </button>
          </div>
        </form>

        <form className="bank-card bank-form transaction-form" onSubmit={handleTransaction}>
          <h3>Deposit or Withdraw</h3>

          <div className="form-group">
            <label>Bank Account</label>
            <select
              value={transactionAccountId}
              onChange={(e) => setTransactionAccountId(e.target.value)}
              disabled={adjustingBalance || bankAccounts.length === 0}
            >
              <option value="">Select bank account</option>
              {bankAccounts.map(account => (
                <option key={account.id} value={account.id}>
                  {account.account_name} - {account.account_number}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Transaction Type</label>
            <div className="transaction-type-toggle">
              <button
                type="button"
                className={`toggle-option deposit ${transactionType === 'deposit' ? 'active' : ''}`}
                onClick={() => setTransactionType('deposit')}
                disabled={adjustingBalance}
              >
                ↓ Deposit
              </button>
              <button
                type="button"
                className={`toggle-option withdraw ${transactionType === 'withdraw' ? 'active' : ''}`}
                onClick={() => setTransactionType('withdraw')}
                disabled={adjustingBalance}
              >
                ↑ Withdraw
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Amount</label>
            <input
              type="number"
              min="0.01"
              step="0.01"
              value={transactionAmount}
              onChange={(e) => setTransactionAmount(e.target.value)}
              placeholder="0.00"
              disabled={adjustingBalance}
              onWheel={(e) => e.target.blur()}
            />
          </div>

          <div className="form-group">
            <label>Description (optional)</label>
            <textarea
              value={transactionNotes}
              onChange={(e) => setTransactionNotes(e.target.value)}
              placeholder="Add a note for the day book"
              rows="3"
              disabled={adjustingBalance}
            />
          </div>

          <div className="action-buttons">
            <button
              type="submit"
              className={`submit-btn ${transactionType === 'withdraw' ? 'submit-btn-withdraw' : ''}`}
              disabled={adjustingBalance || bankAccounts.length === 0}
            >
              {adjustingBalance ? 'Saving...' : transactionType === 'deposit' ? 'Record Deposit' : 'Record Withdrawal'}
            </button>
          </div>
        </form>
      </div>

      {statementAccount && (
        <div className="statement-modal-overlay" onClick={closeStatement}>
          <div className="statement-modal" onClick={(e) => e.stopPropagation()}>
            <div className="statement-modal-header">
              <h3>
                Statement — {statementAccount.account_name} ({statementAccount.account_number})
              </h3>
              <button type="button" className="icon-btn" onClick={closeStatement} title="Close" aria-label="Close">
                ✕
              </button>
            </div>

            <div className="statement-filters">
              <div className="form-group">
                <label>From</label>
                <input
                  type="date"
                  value={statementStartDate}
                  onChange={(e) => setStatementStartDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  disabled={statementLoading}
                />
                 <label>To</label>
                <input
                  type="date"
                  value={statementEndDate}
                  onChange={(e) => setStatementEndDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  disabled={statementLoading}
                />
              </div>
          
              <div className="statement-filter-actions">
                <button
                  type="button"
                  className="secondary-btn"
                  onClick={handleApplyStatementRange}
                  disabled={statementLoading || (!statementStartDate && !statementEndDate)}
                >
                  Apply
                </button>
                <button
                  type="button"
                  className="link-btn"
                  onClick={handleShowAllTime}
                  disabled={statementLoading || (!statementStartDate && !statementEndDate)}
                >
                  View All (since account creation)
                </button>
              </div>
            </div>

            {statementLoading ? (
              <p className="statement-status">Loading statement...</p>
            ) : statementError ? (
              <div className="message error">{statementError}</div>
            ) : statement ? (
              <>
                <div className="statement-summary">
                  <div>
                    <span className="label">Opening Balance</span>
                    <div className="value">₹ {formatAmount(statement.opening_balance)}</div>
                  </div>
                  <div>
                    <span className="label">Closing Balance</span>
                    <div className="value">₹ {formatAmount(statement.closing_balance)}</div>
                  </div>
                </div>

                {statement.entries.length === 0 ? (
                  <div className="empty-state">
                    <p className="no-account">No deposits or withdrawals recorded for this account yet.</p>
                  </div>
                ) : (
                  <div className="statement-table-wrap">
                    <table className="statement-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Amount</th>
                          <th>Balance</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statement.entries.map(entry => (
                          <tr key={entry.id}>
                            <td>{new Date(entry.date).toLocaleDateString('en-IN')}</td>
                            <td>
                              <span className={`entry-badge ${entry.type === 'deposit' ? 'entry-deposit' : 'entry-withdraw'}`}>
                                {entry.type === 'deposit' ? '↓ Deposit' : '↑ Withdraw'}
                              </span>
                            </td>
                            <td>₹ {formatAmount(entry.amount)}</td>
                            <td>₹ {formatAmount(entry.balance_after)}</td>
                            <td className="entry-notes">{entry.notes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default BankBalance;
